import { Role } from "@models/role";
import { useMemo } from "react";
import { useOrganizations, useOrganizationUsers } from "./organization";

/**
 * Hook to get total classrooms across all organizations
 * Returns 0 as default while loading or if no organizations
 */
export function useTotalClassrooms(orgCode) {
  const { data: organizations, isLoading } = useOrganizations(orgCode, {
    enabled: !!orgCode,
  });

  return useMemo(() => {
    if (isLoading || !organizations || organizations.length === 0) return 0;
    return organizations.reduce((p, c) => p + (c.totalClassrooms || 0), 0);
  }, [organizations, isLoading]);
}

/**
 * Hook to get total instructors across all organizations
 * Returns 0 as default while loading or if no organizations
 */
export function useTotalInstructors(orgCode) {
  const { data: organizations, isLoading } = useOrganizations(orgCode, {
    enabled: !!orgCode,
  });

  return useMemo(() => {
    if (isLoading || !organizations || organizations.length === 0) return 0;
    return organizations.reduce((p, c) => p + (c.totalInstructors || 0), 0);
  }, [organizations, isLoading]);
}

/**
 * Hook to get total trainees across all organizations
 * Returns 0 as default while loading or if no organizations
 */
export function useTotalTrainees(orgCode) {
  const { data: organizations, isLoading } = useOrganizations(orgCode, {
    enabled: !!orgCode,
  });

  return useMemo(() => {
    if (isLoading || !organizations || organizations.length === 0) return 0;
    return organizations.reduce((p, c) => p + (c.totalTrainees || 0), 0);
  }, [organizations, isLoading]);
}

/**
 * Hook to get students filtered from organization users
 * Returns empty array as default while loading or if no users
 */
export function useStudents({ role, orgCode, userId }) {
  const { data: users, isLoading } = useOrganizationUsers(
    { role, orgCode, userId },
    {
      enabled: !!(role && orgCode && userId),
    }
  );

  return useMemo(() => {
    if (isLoading || !users) return [];
    return users.filter((u) => u.roleId === Role.STUDENT.id);
  }, [users, isLoading]);
}

/**
 * Hook to get organization by code
 */
export function useOrganizationByCode(orgCode) {
  const { data: organizations } = useOrganizations(orgCode, {
    enabled: !!orgCode,
  });

  return useMemo(
    () => organizations?.find((o) => o.code === orgCode),
    [organizations, orgCode]
  );
}
